import OlMap from 'ol/Map';
import Feature, { FeatureLike } from 'ol/Feature';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import WKT from 'ol/format/WKT';
import { Coordinate } from 'ol/coordinate';
import { Extent, createEmpty, extend, getCenter, isEmpty } from 'ol/extent';
import { Fill, Stroke, Style } from 'ol/style';
import { firstValueFrom } from 'rxjs';
import { BlastPolygonWKTDto, PolygonType } from 'src/app/bo/polygon/tirsPolygone';
import { HttpService } from 'src/app/services/http.service';
import {
  ALL_POLYGON_TYPES,
  DEFAULT_POLYGON_FILL,
  FEATURE_BLAST_ID,
  FEATURE_KIND,
  FEATURE_POLYGON_ID,
  FEATURE_POLYGON_TYPE,
  MAP_PROJECTION,
  Z_INDEX,
} from '../site-map.constants';
import { ensureProjection } from '../utils/projection';

/** L'API sérialise BlastId comme un type fort ({ value }) alors que le DTO TypeScript le déclare en number. */
export function normalizeBlastId(raw: unknown): number {
  return typeof raw === 'object' && raw !== null ? Number((raw as { value: unknown }).value) : Number(raw);
}

export class BlastPolygonsLayer {
  private readonly source = new VectorSource<Feature>();
  private readonly layer = new VectorLayer({
    source: this.source,
    zIndex: Z_INDEX.blastPolygons,
    style: feature => this.styleFor(feature),
  });
  private readonly wkt = new WKT();
  private readonly requestedBlastIds = new Set<number>();
  private readonly pendingLoads = new Map<number, Promise<void>>();
  private readonly styleCache = new Map<string, Style>();

  private siteId: number | null = null;
  private generation = 0;
  private visibleBlastIds = new Set<number>();
  private visibleTypes = new Set<PolygonType>();
  private colorOf: (blastId: number) => string = () => DEFAULT_POLYGON_FILL;
  private hovered: FeatureLike | null = null;

  constructor(private readonly map: OlMap, private readonly http: HttpService) {
    map.addLayer(this.layer);
  }

  /**
   * Affiche les polygones des tirs demandés et charge, tous types confondus, ceux qui ne l'ont pas encore été.
   * La promesse se résout quand tous les tirs demandés sont chargés, y compris ceux dont le chargement était déjà en cours.
   */
  async showBlasts(siteId: number, blastIds: number[]): Promise<void> {
    if (siteId !== this.siteId) this.resetSite(siteId);
    this.visibleBlastIds = new Set(blastIds);
    this.layer.changed();

    const missing = blastIds.filter(id => !this.requestedBlastIds.has(id));
    if (missing.length) {
      const load = this.loadPolygons(siteId, missing, this.generation);
      missing.forEach(id => {
        this.requestedBlastIds.add(id);
        this.pendingLoads.set(id, load);
      });
    }
    const loads = new Set(blastIds.map(id => this.pendingLoads.get(id)).filter(load => !!load));
    await Promise.all(loads);
  }

  /** Recharge les polygones d'un tir, par exemple après la création d'un nouveau contour. */
  async reloadBlast(siteId: number, blastId: number): Promise<void> {
    // Un chargement initial encore en cours repeuplerait la couche après le nettoyage : on le laisse finir d'abord.
    await this.pendingLoads.get(blastId);
    this.source.getFeatures()
      .filter(feature => feature.get(FEATURE_BLAST_ID) === blastId)
      .forEach(feature => this.source.removeFeature(feature));
    this.requestedBlastIds.delete(blastId);
    await this.showBlasts(siteId, [...this.visibleBlastIds]);
  }

  setVisibleTypes(types: PolygonType[]): void {
    this.visibleTypes = new Set(types);
    this.layer.changed();
  }

  setColoring(colorOf: (blastId: number) => string): void {
    this.colorOf = colorOf;
    this.styleCache.clear();
    this.layer.changed();
  }

  setHovered(feature: FeatureLike | null): void {
    if (feature === this.hovered) return;
    this.hovered = feature;
    this.layer.changed();
  }

  /** Tirs affichés dont un polygone recoupe l'emprise, quel que soit le type de polygone filtré. */
  blastIdsInExtent(extent: Extent): number[] {
    const blastIds = new Set<number>();
    this.source.forEachFeatureInExtent(extent, feature => {
      const blastId = feature.get(FEATURE_BLAST_ID);
      if (this.visibleBlastIds.has(blastId)) blastIds.add(blastId);
    });
    return [...blastIds];
  }

  blastCenter(blastId: number): Coordinate | null {
    const feature = this.source.getFeatures().find(candidate => candidate.get(FEATURE_BLAST_ID) === blastId);
    const geometry = feature?.getGeometry();
    return geometry ? getCenter(geometry.getExtent()) : null;
  }

  /** Emprise cumulée de tous les polygones du tir, tous types confondus. */
  blastExtent(blastId: number): Extent | null {
    const extent = createEmpty();
    this.source.getFeatures()
      .filter(feature => feature.get(FEATURE_BLAST_ID) === blastId)
      .forEach(feature => {
        const geometry = feature.getGeometry();
        if (geometry) extend(extent, geometry.getExtent());
      });
    return isEmpty(extent) ? null : extent;
  }

  dispose(): void {
    this.generation++;
    this.map.removeLayer(this.layer);
    this.source.clear();
    this.styleCache.clear();
  }

  private resetSite(siteId: number): void {
    this.generation++;
    this.siteId = siteId;
    this.requestedBlastIds.clear();
    this.pendingLoads.clear();
    this.source.clear();
    this.hovered = null;
  }

  private async loadPolygons(siteId: number, blastIds: number[], generation: number): Promise<void> {
    try {
      const polygons = await firstValueFrom(this.http.post<object, BlastPolygonWKTDto[]>(
        `TirsPolygone/${siteId}/tirsPolygoneByIdTir`,
        { idsTirs: blastIds, idsTirsPolygoneType: ALL_POLYGON_TYPES },
      ));
      await this.addPolygons(polygons ?? [], generation);
    } catch {
      if (generation === this.generation) blastIds.forEach(id => this.requestedBlastIds.delete(id));
    } finally {
      if (generation === this.generation) blastIds.forEach(id => this.pendingLoads.delete(id));
    }
  }

  private async addPolygons(polygons: BlastPolygonWKTDto[], generation: number): Promise<void> {
    const epsgCodes = [...new Set(polygons.map(polygon => polygon.epsg))];
    await Promise.all(epsgCodes.map(epsg => ensureProjection(this.http, epsg)));
    if (generation !== this.generation) return;

    const features = polygons
      .map(polygon => this.toFeature(polygon))
      .filter((feature): feature is Feature => feature !== null);
    this.source.addFeatures(features);
  }

  private toFeature(polygon: BlastPolygonWKTDto): Feature | null {
    try {
      const feature = this.wkt.readFeature(polygon.wktPolygon, {
        dataProjection: `EPSG:${polygon.epsg}`,
        featureProjection: MAP_PROJECTION,
      });
      feature.setProperties({
        [FEATURE_KIND]: 'blast-polygon',
        [FEATURE_BLAST_ID]: normalizeBlastId(polygon.blastId),
        [FEATURE_POLYGON_TYPE]: Number(polygon.type),
        [FEATURE_POLYGON_ID]: polygon.blastPolygonId,
      }, true);
      return feature;
    } catch {
      return null;
    }
  }

  private styleFor(feature: FeatureLike): Style | undefined {
    const blastId = feature.get(FEATURE_BLAST_ID);
    const type: PolygonType = feature.get(FEATURE_POLYGON_TYPE);
    if (!this.visibleBlastIds.has(blastId) || !this.visibleTypes.has(type)) return undefined;

    const color = this.colorOf(blastId);
    if (feature === this.hovered) {
      return this.cachedStyle(`hover|${color}`, () => new Style({
        fill: new Fill({ color }),
        stroke: new Stroke({ color: 'rgba(255, 255, 255, 0.85)', width: 3 }),
        zIndex: 5,
      }));
    }
    if (type === PolygonType.DangerZone) {
      return this.cachedStyle('danger', () => new Style({
        fill: new Fill({ color: 'rgba(255, 192, 203, 0.6)' }),
        stroke: new Stroke({ color: 'black', width: 1, lineDash: [4] }),
        zIndex: 3,
      }));
    }
    const outline = type === PolygonType.DrillingContour ? 'rgba(200, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.5)';
    return this.cachedStyle(`${outline}|${color}`, () => new Style({
      fill: new Fill({ color }),
      stroke: new Stroke({ color: outline, width: 2 }),
      zIndex: 4,
    }));
  }

  private cachedStyle(key: string, create: () => Style): Style {
    let style = this.styleCache.get(key);
    if (!style) {
      style = create();
      this.styleCache.set(key, style);
    }
    return style;
  }
}
